import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { PageHeader } from "../../../components/ui";
import {
  CaseBreakdownReport,
  ClioService,
  LloCaseBreakdownRow,
  OpenAPI,
  PloCaseBreakdownRow,
} from "../../../api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.style.display = "none";
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
}

const ploColumns: { key: keyof PloCaseBreakdownRow; label: string }[] = [
  { key: "attorney", label: "Attorney" },
  { key: "casesInFunnels", label: "Cases in Funnels (excl. Nego/Term/Lit Transition)" },
  { key: "negotiations1To30", label: "Nego 1-30" },
  { key: "negotiations31To60", label: "Nego 31-60" },
  { key: "negotiations61To90", label: "Nego 61-90" },
  { key: "negotiations91To120", label: "Nego 91-120" },
  { key: "negotiations121Plus", label: "Nego 121+" },
  { key: "casesInTermination", label: "Termination" },
  { key: "casesInLitigationTransition", label: "Lit Transition" },
  { key: "totalPloExcludingTermAndLitTransition", label: "Total PLO (excl. Term/Lit Transition)" },
  { key: "lloCases", label: "LLO Cases" },
  { key: "totalPloPlusLlo", label: "Total PLO + LLO" },
];

const lloColumns: { key: keyof LloCaseBreakdownRow; label: string }[] = [
  { key: "teamMember", label: "Team Member" },
  { key: "casesInFunnel", label: "Cases in Funnel (excl. Ready/Filed)" },
  { key: "readyToBeFiled", label: "Ready to be Filed" },
  { key: "casesFiled", label: "Cases Filed" },
  { key: "discoveryRequestsServed", label: "Discovery Requests Served" },
  { key: "total", label: "Total" },
];

export default function CaseBreakdownReportPage() {
  const [report, setReport] = useState<CaseBreakdownReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ClioService.getApiClioCaseBreakdownReport();
      setReport(data);
    } catch {
      await MySwal.fire({
        icon: "error",
        title: <p>Error loading Case Breakdown report.</p>,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const downloadReport = useCallback(async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/caseBreakdownReport/download`, {
        method: "GET",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
        },
      });
      if (!response.ok) {
        throw new Error("Download failed");
      }
      const blob = await response.blob();
      triggerBlobDownload(blob, `CaseBreakdownReport-${dayjs().format("YYYY-MM-DD")}.xlsx`);
    } catch {
      await MySwal.fire({
        icon: "error",
        title: <p>Error downloading Case Breakdown report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Case Breakdown Report"
        subtitle="Pre-litigation attorney caseload and LLO team-member breakdown"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => void loadReport()}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              onClick={() => void downloadReport()}
              disabled={downloading || loading}
            >
              Download Excel
            </Button>
          </Stack>
        }
      />

      {report?.lastUpdateLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {report.lastUpdateLabel}
          {report.asOf ? ` · ${dayjs(report.asOf).format("MMM D, YYYY h:mm A")}` : ""}
        </Typography>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Pre-litigation
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {ploColumns.map((col) => (
                <TableCell key={col.key}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(report?.preLitigation ?? []).map((row) => (
              <TableRow key={row.attorney}>
                {ploColumns.map((col) => (
                  <TableCell key={col.key}>{row[col.key] ?? 0}</TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              {ploColumns.slice(1, -1).map((col) => (
                <TableCell key={col.key} />
              ))}
              <TableCell sx={{ fontWeight: 700 }}>
                {report?.preLitigationGrandTotal ?? 0}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        LLO
      </Typography>
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {lloColumns.map((col) => (
                <TableCell key={col.key}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(report?.llo ?? []).map((row) => (
              <TableRow key={row.teamMember}>
                {lloColumns.map((col) => (
                  <TableCell key={col.key}>{row[col.key] ?? 0}</TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              {lloColumns.slice(1, -1).map((col) => (
                <TableCell key={col.key} />
              ))}
              <TableCell sx={{ fontWeight: 700 }}>{report?.lloGrandTotal ?? 0}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
